// ─── Terminal interactif (canal « terminal », xterm.js) ───
// Compte SYSTEM ou usager de la session assistée, PowerShell ou cmd. Un
// changement de compte ou de shell rouvre le canal (nouveau shell côté poste).

import { useEffect, useRef, useState } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useChannel } from './useChannel.js';

const sel = 'bg-card border border-rule-3 rounded-lg px-2 py-1 text-xs text-fg-2 cursor-pointer';

export function Terminal({ sessionId, visible, capabilities }) {
  const conteneur = useRef(null);
  const term = useRef(null);
  const fit = useRef(null);
  const [compte, setCompte] = useState('systeme');
  const [shell, setShell] = useState('powershell');
  const [demarre, setDemarre] = useState(false);
  const choixCompte = capabilities.includes('terminal_usager');

  // Le terminal n'est créé (et le canal ouvert) qu'à la première ouverture du panneau.
  useEffect(() => {
    if (visible) setDemarre(true);
  }, [visible]);

  const canal = useChannel(
    sessionId,
    'terminal',
    (e) => {
      if (typeof e.data !== 'string') return;
      let m;
      try { m = JSON.parse(e.data); } catch { return; }
      if (m.t === 'sortie') term.current?.write(m.donnees);
      else if (m.t === 'fin') term.current?.write(`\r\n\x1b[90m[Fin du processus, code ${m.code}]\x1b[0m\r\n`);
    },
    (send) => {
      const t = term.current;
      send({ t: 'ouvrir', compte, shell, colonnes: t?.cols || 120, lignes: t?.rows || 30 });
    },
    { enabled: demarre, restartKey: compte + '|' + shell },
  );

  const envoyerRef = useRef(canal.envoyer);
  envoyerRef.current = canal.envoyer;

  useEffect(() => {
    if (!demarre || !conteneur.current) return undefined;
    const t = new XTerm({
      fontFamily: '"Cascadia Mono", Consolas, "DejaVu Sans Mono", monospace',
      fontSize: 14,
      cursorBlink: true,
      scrollback: 5000,
      theme: { background: '#0f1720', foreground: '#e6edf3' },
    });
    const f = new FitAddon();
    t.loadAddon(f);
    t.open(conteneur.current);
    term.current = t;
    fit.current = f;
    try { f.fit(); } catch { /* conteneur masqué */ }
    const saisie = t.onData((donnees) => envoyerRef.current({ t: 'entree', donnees }));
    const taille = t.onResize(({ cols, rows }) => envoyerRef.current({ t: 'taille', colonnes: cols, lignes: rows }));
    const obs = new ResizeObserver(() => {
      try { if (conteneur.current?.offsetWidth) f.fit(); } catch { /* ignoré */ }
    });
    obs.observe(conteneur.current);
    return () => {
      obs.disconnect();
      saisie.dispose();
      taille.dispose();
      t.dispose();
      term.current = null;
    };
  }, [demarre]);

  // Nouveau shell : on repart d'un écran propre avec un message de connexion.
  useEffect(() => {
    term.current?.reset();
    term.current?.write('\x1b[90mConnexion à l’invite de commandes du poste…\x1b[0m\r\n');
  }, [compte, shell, demarre]);

  useEffect(() => {
    if (canal.etat === 'ferme' && canal.raison) {
      term.current?.write(`\r\n\x1b[33m${canal.raison}\x1b[0m\r\n`);
    }
  }, [canal.etat, canal.raison]);

  useEffect(() => {
    if (!visible) return;
    requestAnimationFrame(() => {
      try { fit.current?.fit(); } catch { /* ignoré */ }
      term.current?.focus();
    });
  }, [visible]);

  return (
    <div className={`${visible ? 'flex' : 'hidden'} flex-col flex-1 min-h-0`}>
      <div className="flex flex-wrap items-center gap-2 p-2 border-b border-rule">
        {choixCompte && (
          <select className={sel} value={compte} onChange={(e) => setCompte(e.target.value)}>
            <option value="systeme">SYSTEM (administrateur)</option>
            <option value="usager">Usager connecté</option>
          </select>
        )}
        <select className={sel} value={shell} onChange={(e) => setShell(e.target.value)}>
          <option value="powershell">PowerShell</option>
          <option value="cmd">Invite de commandes (cmd)</option>
        </select>
        <span className="text-[11px] text-muted-2">
          {compte === 'usager'
            ? 'Ses lecteurs réseau, son profil et ses droits à lui.'
            : 'Tous les droits sur le poste, sans les lecteurs réseau de l’usager.'}
        </span>
      </div>
      <div className="flex-1 min-h-0 p-2" style={{ background: '#0f1720' }}>
        <div ref={conteneur} className="h-full" />
      </div>
    </div>
  );
}
